import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import { validatePassword, shouldEnforcePasswordPolicy, generateSecurePassword } from '../utils/passwordPolicy';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { logAuthEvent, EVENT_TYPES } from '../utils/securityLogger';
import './ResetPasswordPage.css';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [oobCode, setOobCode] = useState('');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState(null);
  const [showPasswordGenerator, setShowPasswordGenerator] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');

  useEffect(() => {
    const code = searchParams.get('oobCode');
    if (!code) {
      setValidationError(t('resetPassword.errorMessages.invalidLink'));
      setIsValidating(false);
      return;
    }

    setOobCode(code);
    verifyResetCode(code);
  }, [searchParams]);

  const verifyResetCode = async (code) => {
    try {
      const auth = getAuth();
      const email = await verifyPasswordResetCode(auth, code);
      setEmail(email);
      setIsValidating(false);
    } catch (error) {
      console.error('Reset code verification error:', error);
      setValidationError(t('resetPassword.errorMessages.invalidLink'));
      setIsValidating(false);
    }
  };

  const validateNewPassword = (password) => {
    if (!password) {
      setPasswordValidation(null);
      return;
    }

    const validation = validatePassword(password, email, '');
    setPasswordValidation(validation);
    return validation;
  };

  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    validateNewPassword(password);
  };

  const generateNewPassword = () => {
    const newPassword = generateSecurePassword(12);
    setGeneratedPassword(newPassword);
    setNewPassword(newPassword);
    validateNewPassword(newPassword);
    setShowPasswordGenerator(false);
  };

  const useGeneratedPassword = () => {
    setNewPassword(generatedPassword);
    validateNewPassword(generatedPassword);
    setShowPasswordGenerator(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setIsResetting(true);

    try {
      // Validate passwords
      if (!newPassword || !confirmPassword) {
        setPasswordError(t('resetPassword.errorMessages.allFieldsRequired'));
        return;
      }

      if (newPassword !== confirmPassword) {
        setPasswordError(t('resetPassword.errorMessages.passwordsDoNotMatch'));
        return;
      }

      // Enforce password policy for password resets
      const shouldEnforce = shouldEnforcePasswordPolicy(false, true);
      if (shouldEnforce) {
        const validation = validatePassword(newPassword, email, '');
        if (!validation.isValid) {
          setPasswordError(t('resetPassword.errorMessages.passwordRequirements'));
          return;
        }
      }

      // Reset password
      const auth = getAuth();
      await confirmPasswordReset(auth, oobCode, newPassword);

      // Log successful password reset
      await logAuthEvent(EVENT_TYPES.PASSWORD_RESET_SUCCESS, null, {
        email: email,
        resetMethod: 'email_link',
        passwordStrength: passwordValidation?.score || 0
      });

      toast.success(t('resetPassword.successMessage'));
      navigate('/login');
    } catch (error) {
      console.error('Password reset error:', error);
      if (error.code === 'auth/invalid-action-code') {
        setPasswordError(t('resetPassword.errorMessages.invalidLink'));
      } else if (error.code === 'auth/weak-password') {
        setPasswordError(t('resetPassword.errorMessages.weakPassword'));
      } else {
        setPasswordError(t('resetPassword.errorMessages.resetFailed'));
      }
    } finally {
      setIsResetting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>{t('resetPassword.verifyingLink')}</p>
        </div>
        </div>
      </div>
    );
  }

  if (validationError) {
    return (
      <div className="reset-password-container">
        <div className="reset-password-card">
        <div className="error-state">
          <h2>❌ {t('resetPassword.invalidLink')}</h2>
          <p>{validationError}</p>
          <button 
            className="action-button"
            onClick={() => navigate('/login')}
          >
            {t('resetPassword.backToLogin')}
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <div className="reset-password-card">
        <div className="reset-password-header">
          <h1>{t('resetPassword.title')}</h1>
          <p>{t('resetPassword.subtitle')} <strong>{email}</strong></p>
        </div>

        <form onSubmit={handleResetPassword} className="reset-password-form">
          <div className="form-group">
            <label>{t('resetPassword.newPassword')}</label>
            <div className="password-input-container">
              <input
                type="password"
                value={newPassword}
                onChange={handlePasswordChange}
                placeholder={t('resetPassword.newPassword')}
                disabled={isResetting}
                required
              />
              <button
                type="button"
                className="password-generator-btn"
                onClick={() => setShowPasswordGenerator(!showPasswordGenerator)}
                disabled={isResetting}
              >
                🎲 {t('passwordPolicy.generator.generateNew')}
              </button>
            </div>
            
            {/* Password Generator Modal */}
            {showPasswordGenerator && (
              <div className="password-generator-modal">
                <div className="generator-content">
                  <h4>{t('passwordPolicy.generator.title')}</h4>
                  <div className="generated-password-display">
                    <input
                      type="text"
                      value={generatedPassword}
                      readOnly
                      className="generated-password-input"
                    />
                    <button
                      type="button"
                      className="copy-password-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(generatedPassword);
                        toast.success(t('passwordPolicy.generator.passwordCopied'));
                      }}
                    >
                      📋 {t('passwordPolicy.generator.copyPassword')}
                    </button>
                  </div>
                  <div className="generator-actions">
                    <button
                      type="button"
                      className="generate-new-btn"
                      onClick={generateNewPassword}
                    >
                      {t('passwordPolicy.generator.generateNew')}
                    </button>
                    <button
                      type="button"
                      className="use-password-btn"
                      onClick={useGeneratedPassword}
                    >
                      {t('passwordPolicy.generator.usePassword')}
                    </button>
                    <button
                      type="button"
                      className="close-generator-btn"
                      onClick={() => setShowPasswordGenerator(false)}
                    >
                      {t('passwordPolicy.generator.cancel')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Password Strength Indicator */}
          {newPassword && (
            <PasswordStrengthIndicator
              password={newPassword}
              email={email}
              username=""
              showRequirements={true}
              showSuggestions={true}
            />
          )}

          <div className="form-group">
            <label>{t('resetPassword.confirmPassword')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('resetPassword.confirmPassword')}
              disabled={isResetting}
              required
            />
          </div>

          {passwordError && (
            <div className="error-message">
              {passwordError}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="reset-button"
              disabled={isResetting || !newPassword || !confirmPassword}
            >
              {isResetting ? t('resetPassword.resetting') : t('resetPassword.resetButton')}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/login')}
              disabled={isResetting}
            >
              {t('resetPassword.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
